import { useEffect, useSyncExternalStore } from 'react';
import { SALARY_PIN_UNLOCK_SECONDS } from '@hrms/shared';
import { queryClient } from '../../lib/query-client.js';
import { tokens, tsr } from '../../lib/api.js';
import { SALARY_PIN_STATUS_QUERY_KEY, SalaryPinDialog } from './SalaryPinDialog.js';
import {
  armRelock,
  cancelUnlock,
  checkSalaryRelock,
  getSnapshot,
  localUnlockEnd,
  onRelock,
  onUnlock,
  relockArmed,
  resetSalaryPinGate,
  resolveUnlock,
  subscribe,
} from './salaryPinGate.js';
import { isSalaryGatedData, isSalaryPinRequiredError } from './salaryPinErrors.js';

/**
 * Mounted ONCE, beside the toaster (main.tsx). It shows the salary PIN prompt whenever the API has
 * refused a pay-bearing request (ADR 0041), and keeps the screen honest about the five-minute unlock:
 *
 *   - On relock, every cached response the API served from behind the PIN (`X-Salary-Pin: gated`) is
 *     reset — the figures leave the screen, and whatever is still on screen asks again.
 *   - On unlock, anything refused earlier (the person closed the prompt) loads again.
 *   - Timers sleep while the phone app is in the background, so coming back to the front checks the
 *     clock too.
 *   - Gated data that arrives with no relock timer armed (a reload inside the five minutes, or an unlock in
 *     another tab of the same login) asks the API when the unlock ends and arms the timer from that, so
 *     those figures relock too.
 *   - Signing out forgets the unlock and closes the prompt.
 */
export function SalaryPinHost() {
  const { pending } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    const stopRelock = onRelock(() => {
      void queryClient.resetQueries({ predicate: (query) => isSalaryGatedData(query.state.data) });
      void queryClient.invalidateQueries({ queryKey: SALARY_PIN_STATUS_QUERY_KEY });
    });
    const stopUnlock = onUnlock(() => {
      void queryClient.invalidateQueries({
        predicate: (query) => isSalaryPinRequiredError(query.state.error),
      });
      void queryClient.invalidateQueries({ queryKey: SALARY_PIN_STATUS_QUERY_KEY });
    });
    // One status read at a time: a payroll page lands a handful of gated responses together.
    let asking = false;
    // The status read failed, so how long is left is unknown. The server just served gated data, so the unlock is
    // at most a full window: relock then. Relocking NOW would reset the gated queries, which refetch, come back
    // gated and ask again: a refetch loop for as long as the status read keeps failing.
    const armUntilKnown = () => armRelock(localUnlockEnd(SALARY_PIN_UNLOCK_SECONDS * 1000));
    const armFromServer = async () => {
      if (asking) return;
      asking = true;
      try {
        const res = await tsr.salaryPin.getStatus.query();
        // Still unarmed (no PIN entered in this tab meanwhile): arm from the time the server says is left,
        // or drop now when it says none is.
        if (relockArmed()) return;
        if (res.status === 200) {
          const left = res.body.unlockedForMs;
          armRelock(left === null ? null : localUnlockEnd(left));
        } else {
          armUntilKnown();
        }
      } catch {
        if (!relockArmed()) armUntilKnown();
      } finally {
        asking = false;
      }
    };
    const stopCache = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || relockArmed()) return;
      if (isSalaryGatedData(event.query.state.data)) void armFromServer();
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkSalaryRelock();
    };
    document.addEventListener('visibilitychange', onVisible);
    const stopTokens = tokens.subscribe(() => {
      if (!tokens.hasSession()) resetSalaryPinGate();
    });
    return () => {
      stopRelock();
      stopUnlock();
      stopCache();
      stopTokens();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return (
    <SalaryPinDialog
      open={pending !== null}
      details={pending}
      onUnlocked={resolveUnlock}
      onCancel={cancelUnlock}
    />
  );
}
